"use client";

import React, { useCallback, useMemo, useState } from "react";
import {
  Card,
  Button as ButtonComponent,
  Modal,
  AlertModal,
  Select,
  Input,
  Textarea,
  Switch,
} from "@/components";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { notificationsBirthdaySchema } from "@/validations";
import { renderTemplate } from "@/utils/templateRenderer";
import { Plus, Pencil, Trash, Eye, Send } from "lucide-react";
import useNotificationsBirthday from "@/hooks/notificationsBirthday/useNotificationsBirthday";
import axiosInstance from "@/utils/axiosInstance";
import { useMe } from "@/hooks";
import type { BirthdayNotificationTemplate } from "@/types/notifications";
import { toast } from "react-toastify";

type Props = { dict: any };

const NotificationsBirthdayManager: React.FC<Props> = ({ dict }) => {
  const {
    templates,
    loading,
    refresh,
    createTemplate,
    updateTemplate,
    removeTemplate,
  } = useNotificationsBirthday(() => refresh());
  const { user: meUser } = useMe();

  // load templates on mount
  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const [editing, setEditing] = useState<BirthdayNotificationTemplate | null>(
    null,
  );
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [toDeleteId, setToDeleteId] = useState<number | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<any>({
    resolver: yupResolver(notificationsBirthdaySchema as any),
    defaultValues: {
      type: "birthday",
      days_before: null,
      title: "",
      body: "",
      active: true,
    },
  });

  const selectedType = watch("type");

  // Force days_before = 0 for birthday (no advance days)
  React.useEffect(() => {
    if (selectedType === "birthday") {
      setValue("days_before", 0);
    }
  }, [selectedType, setValue]);

  const openCreate = useCallback(() => {
    setEditing(null);
    reset({
      type: "birthday",
      days_before: null,
      title: "",
      body: "",
      active: true,
    });
    setOpenForm(true);
  }, [reset]);

  const openEdit = useCallback(
    (tpl: any) => {
      setEditing(tpl);
      reset({ ...tpl });
      setOpenForm(true);
    },
    [reset],
  );

  const onSubmit = useCallback(
    async (data: any) => {
      const payload =
        data.type === "birthday" ? { ...data, days_before: 0 } : data;

      if (editing && editing.id) {
        await updateTemplate({ id: editing.id, ...payload });
      } else {
        await createTemplate(payload);
      }
      reset();
      setOpenForm(false);
    },
    [editing, createTemplate, updateTemplate, reset],
  );

  const handleDelete = useCallback((tpl: any) => {
    setToDeleteId(tpl.id);
    setOpenDeleteConfirm(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (toDeleteId != null) await removeTemplate(toDeleteId);
    setToDeleteId(null);
    setOpenDeleteConfirm(false);
  }, [toDeleteId, removeTemplate]);

  const handleToggleActive = useCallback(
    async (tpl: BirthdayNotificationTemplate) => {
      const payload =
        tpl.type === "birthday"
          ? { ...tpl, active: !tpl.active, days_before: 0 }
          : { ...tpl, active: !tpl.active };
      await updateTemplate({ id: tpl.id as number, ...payload });
    },
    [updateTemplate],
  );

  const handlePreview = useCallback((tplStr: string) => {
    const example = {
      first_name: "Ana",
      name: "Ana Silva",
      establishment: "Meu Salão",
      days: 7,
    };
    setPreview(renderTemplate(tplStr || "", example));
  }, []);

  const handleSendTest = useCallback(
    async (body: string, title?: string) => {
      const notifiableId = (meUser as any)?.id;

      if (!notifiableId) {
        toast.error(
          dict?.["Failed to send notification"] ||
            "Failed to send notification",
        );
        return;
      }

      try {
        const { data } = await axiosInstance.post("/send-notification", {
          notifiable_id: notifiableId,
          title: title || "",
          body,
        });

        const successMessage =
          data?.message ||
          dict?.["Notification sent successfully"] ||
          "Notification sent successfully";
        toast.success(successMessage);
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.message ||
          dict?.["Failed to send notification"] ||
          "Failed to send notification";
        toast.error(errorMessage);
      }
    },
    [meUser, dict],
  );

  const messageFieldError = (() => {
    const m = (errors as any)?.body;
    if (!m) return null;
    if (typeof m === "string") return m;
    if (typeof m?.message === "string") return m.message;
    return String(m);
  })();

  const rows = useMemo(() => templates || [], [templates]);

  const typeOptions = useMemo(
    () => [
      { value: "birthday", label: dict["Birthday"] || "birthday" },
      { value: "pre_birthday", label: dict["Pre-birthday"] || "pre_birthday" },
    ],
    [dict],
  );

  const typeLabelMap = useMemo<Record<string, string>>(
    () => ({
      birthday: dict["Birthday"] || "birthday",
      pre_birthday: dict["Pre-birthday"] || "pre_birthday",
    }),
    [dict],
  );

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <ButtonComponent
          onClick={openCreate}
          icon={<Plus />}
          text={dict["New Template"] || "New Template"}
          variant="secondary"
        />
      </div>

      <div className="w-full overflow-auto">
        <table className="w-full table-auto text-sm">
          <thead>
            <tr className="text-left">
              <th className="p-2">{dict["Template Title"] || "Title"}</th>
              <th className="p-2">{dict.Type || "Type"}</th>
              <th className="p-2">{dict["Days before"] || "Days before"}</th>
              <th className="p-2">{dict.Active || "Active"}</th>
              <th className="p-2">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="p-6 text-center text-muted-foreground"
                >
                  {dict["No templates"] || "No templates found."}
                </td>
              </tr>
            ) : (
              rows.map((tpl: any) => (
                <tr key={tpl.id} className="border-t">
                  <td className="p-2 align-top">
                    {tpl.title || (tpl.body || "").slice(0, 60)}
                  </td>
                  <td className="p-2 align-top">
                    {typeLabelMap[tpl.type as keyof typeof typeLabelMap] ||
                      tpl.type}
                  </td>
                  <td className="p-2 align-top">
                    {tpl.type === "birthday" ? "-" : (tpl.days_before ?? "-")}
                  </td>
                  <td className="p-2 align-top">
                    <Switch
                      checked={!!tpl.active}
                      onChange={() => handleToggleActive(tpl)}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <div className="flex gap-2">
                      <button
                        className="btn btn-ghost"
                        onClick={() => handlePreview(tpl.body)}
                        title={dict.Preview}
                      >
                        <Eye size={18} className="cursor-pointer" />
                      </button>
                      <button
                        className="btn btn-ghost"
                        onClick={() => openEdit(tpl)}
                        title={dict.Edit}
                      >
                        <Pencil size={18} className="cursor-pointer" />
                      </button>
                      <button
                        className="btn btn-ghost text-destructive"
                        onClick={() => handleDelete(tpl)}
                        title={dict["Delete template?"]}
                      >
                        <Trash size={18} className="cursor-pointer" />
                      </button>
                      <button
                        className="btn btn-ghost"
                        onClick={() => handleSendTest(tpl.body, tpl.title)}
                        title={dict["Send test"] || "Send test"}
                        aria-label={dict["Send test"] || "Send test"}
                      >
                        <Send
                          size={18}
                          className="text-green-500 cursor-pointer"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        titleModal={
          editing ? dict.Edit || "Edit" : dict["New Template"] || "New Template"
        }
        open={openForm}
        onOpenChange={setOpenForm}
      >
        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="max-w-xl">
            <div className="grid gap-4">
              <div>
                <label className="block text-sm mb-1">
                  {dict.Type || "Type"}
                </label>
                <Select {...register("type")} options={typeOptions} />
              </div>

              {selectedType !== "birthday" && (
                <div>
                  <label className="block text-sm mb-1">
                    {dict["Days before"] || "Days before"}
                  </label>
                  <Input
                    type="number"
                    {...register("days_before", { valueAsNumber: true })}
                  />
                  <div className="text-xs text-destructive mt-1">
                    {(errors as any)?.days_before?.message || null}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm mb-1">
                  {dict.Title || "Title"}
                </label>
                <Input {...register("title")} />
                <div className="text-xs text-destructive mt-1">
                  {(errors as any)?.title?.message || null}
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1">
                  {dict.Body || "Body"}
                </label>
                <Textarea {...register("body")} maxLength={500} rows={4} />
                <div className="text-xs text-destructive mt-1">
                  {(errors as any)?.body?.message || null}
                </div>
                <div className="text-xs text-muted-foreground border rounded bg-muted/30 p-3">
                  {dict["Birthday placeholders help"]}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="block text-sm">
                  {dict.Active || "Active"}
                </label>
                <Switch
                  checked={!!watch("active")}
                  onChange={(v) => setValue("active", v)}
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <ButtonComponent
                  text={dict.Cancel || "Cancel"}
                  variant="default"
                  onClick={() => {
                    reset();
                    setOpenForm(false);
                  }}
                  type="button"
                />
                <ButtonComponent
                  text={dict.Save || "Save"}
                  variant="success"
                  type="submit"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </form>
        </Card>
      </Modal>

      <Modal
        titleModal={dict.Preview || "Preview"}
        open={!!preview}
        onOpenChange={() => setPreview(null)}
      >
        <Card>
          <div className="whitespace-pre-wrap">{preview}</div>
        </Card>
      </Modal>

      <AlertModal
        messageAlert={dict["Are you sure you want to delete this template?"]}
        open={openDeleteConfirm}
        onOpenChange={setOpenDeleteConfirm}
        confirm={confirmDelete}
        cancel={() => setOpenDeleteConfirm(false)}
      />
    </div>
  );
};

export default NotificationsBirthdayManager;
